import { authorizedFetch, getToken } from './session';
import { escapeHtml } from './format';

interface AchievementSummary {
  id: string;
  name: string;
  description: string | null;
  xp: number | null;
  rewardSoft: number | null;
}

interface AchievementsCheckResponse {
  achievements: AchievementSummary[];
  newlyEarned: string[];
}

/**
 * Real-time achievement celebration (owner brief, 2026-08-18: "I want the
 * achievements to actually pop up... more vibes"). `GET /api/achievements`
 * evaluates achievements lazily and grants any newly-earned ones through the
 * XP ledger on that same call (achievements-routes.ts) — it was already the
 * only place "did I just unlock something" is ever computed; the only thing
 * missing was ever calling it right after a game ends and showing the
 * answer, instead of only rendering it on the dedicated Achievements page a
 * player might never visit that session.
 *
 * Fire-and-forget by design: call this after any match/run result finishes
 * rendering. A guest (no token) or a network hiccup just means no toast —
 * never blocks or throws into the caller's own result-rendering path.
 */
export function checkForNewAchievements(): void {
  if (!getToken()) return;
  void authorizedFetch<AchievementsCheckResponse>('/api/achievements')
    .then((data) => {
      if (data.newlyEarned.length === 0) return;
      const byId = new Map(data.achievements.map((a) => [a.id, a]));
      // Staggered, not simultaneous — a double unlock reads as two distinct
      // moments worth celebrating, not one cluttered pile-up.
      data.newlyEarned.forEach((id, index) => {
        const achievement = byId.get(id);
        if (achievement) window.setTimeout(() => showAchievementToast(achievement), index * 550);
      });
    })
    .catch(() => {
      /* best-effort celebration only; the achievement itself is already granted server-side regardless */
    });
}

let toastRoot: HTMLDivElement | null = null;

function getToastRoot(): HTMLDivElement {
  if (toastRoot && document.body.contains(toastRoot)) return toastRoot;
  toastRoot = document.createElement('div');
  toastRoot.className = 'achievement-toast-root';
  toastRoot.setAttribute('aria-live', 'polite');
  document.body.appendChild(toastRoot);
  return toastRoot;
}

function showAchievementToast(a: AchievementSummary): void {
  const root = getToastRoot();
  const rewardLine = [a.xp ? `+${a.xp} XP` : null, a.rewardSoft ? `+${a.rewardSoft} Ticks` : null].filter(Boolean).join(' · ');

  const toast = document.createElement('div');
  toast.className = 'achievement-toast';
  toast.setAttribute('role', 'status');
  toast.innerHTML = `
    <div class="achievement-toast__burst" aria-hidden="true"></div>
    <div class="achievement-toast__icon" aria-hidden="true">🏆</div>
    <div class="achievement-toast__body">
      <span class="achievement-toast__label">Achievement unlocked</span>
      <strong class="achievement-toast__name">${escapeHtml(a.name)}</strong>
      ${a.description ? `<span class="achievement-toast__desc">${escapeHtml(a.description)}</span>` : ''}
      ${rewardLine ? `<span class="achievement-toast__reward">${escapeHtml(rewardLine)}</span>` : ''}
    </div>
  `;

  root.appendChild(toast);
  // Force a reflow so the enter transition actually runs (adding the class
  // in the same tick the element is inserted would just skip straight to
  // its end state).
  void toast.offsetWidth;
  toast.classList.add('is-visible');

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    toast.classList.remove('is-visible');
    toast.classList.add('is-leaving');
    window.setTimeout(() => toast.remove(), 400);
  };
  toast.addEventListener('click', dismiss);
  window.setTimeout(dismiss, 5200);
}
